import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Toolbar, Typography, Button, Container, Box, ButtonGroup } from '@mui/material';

import Dashboard from './components/Dashboard';
import ContentUpload from './components/ContentUpload';
import ContentValidation from './components/ContentValidation';
import Profile from './components/Profile';
import Navigation from './components/Navigation';
import { connectWallet, getContracts } from './utils/contracts';
import { DemoProvider, useDemo } from './context/DemoContext';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#9c27b0', // Deep Purple
      light: '#ba68c8', // Lighter Purple
      dark: '#7b1fa2', // Darker Purple
    },
    secondary: {
      main: '#e1bee7', // Light Purple
      light: '#f3e5f5',
      dark: '#ce93d8',
    },
    background: {
      default: '#170b1a', // Very Dark Purple
      paper: '#2a1533', // Dark Purple Background
    },
    text: {
      primary: '#ffffff',
      secondary: '#e1bee7',
    },
    divider: '#4a2855',
    action: {
      active: '#9c27b0',
      hover: '#7b1fa2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h2: {
      color: '#ba68c8',
    },
    h3: {
      color: '#9c27b0',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(45deg, #170b1a 0%, #2a1533 100%)',
          boxShadow: '0 3px 5px 2px rgba(156, 39, 176, .3)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px 2px rgba(156, 39, 176, .2)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #2a1533 0%, #351b40 100%)',
          borderRadius: 16,
          boxShadow: '0 4px 8px rgba(156, 39, 176, 0.2)',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(156, 39, 176, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #2a1533 0%, #351b40 100%)',
          borderRadius: 16,
        },
      },
    },
  },
});

function AppContent() {
  const [wallet, setWallet] = useState(null);
  const [contracts, setContracts] = useState(null);
  const { isDemoMode, demoWallet, enableDemoMode, disableDemoMode } = useDemo();
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);

  useEffect(() => {
    // Check if MetaMask is installed
    const checkMetaMask = async () => {
      const isInstalled = typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
      setIsMetaMaskInstalled(isInstalled);

      // Listen for account changes
      if (isInstalled) {
        window.ethereum.on('accountsChanged', () => {
          // Reset wallet when account changes
          setWallet(null);
          setContracts(null);
        });
      }
    };

    checkMetaMask();

    // Cleanup listener
    return () => {
      if (window.ethereum && window.ethereum.isMetaMask) {
        window.ethereum.removeListener('accountsChanged', () => {
          setWallet(null);
          setContracts(null);
        });
      }
    };
  }, []);

  const handleConnect = async () => {
    if (!window.ethereum || !window.ethereum.isMetaMask) {
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    try {
      const walletData = await connectWallet();
      setWallet(walletData);
      const contractsData = await getContracts(walletData.signer);
      setContracts(contractsData);
    } catch (error) {
      console.error('Failed to connect:', error);
      if (error.code === 4001) {
        // User rejected the connection request
        alert('Please connect your MetaMask wallet to use this application.');
      } else {
        alert('Failed to connect wallet. Please try again.');
      }
    }
  };

  const handleDemoToggle = () => {
    if (isDemoMode) {
      disableDemoMode();
    } else {
      enableDemoMode();
    }
  };

  const effectiveWallet = isDemoMode ? demoWallet : wallet;

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #170b1a 0%, #2a1533 100%)',
      }}
    >
      <AppBar position="static">
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              background: 'linear-gradient(45deg, #e1bee7 30%, #ffffff 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
            }}
          >
            SoulSig Trust System
          </Typography>
          <ButtonGroup
            variant="contained"
            sx={{
              mr: 2,
              '& .MuiButton-root': {
                borderColor: 'rgba(156, 39, 176, 0.5)',
              },
            }}
          >
            <Button color={isDemoMode ? 'secondary' : 'primary'} onClick={handleDemoToggle}>
              {isDemoMode ? 'Exit Demo' : 'Try Demo'}
            </Button>
            {!isDemoMode && !wallet && (
              <Button color="primary" onClick={handleConnect}>
                {'Connect to your wallet'}
              </Button>
            )}
          </ButtonGroup>
          {effectiveWallet && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.secondary.light,
                background: 'rgba(156, 39, 176, 0.1)',
                padding: '4px 12px',
                borderRadius: '16px',
                border: '1px solid rgba(156, 39, 176, 0.2)',
              }}
            >
              Connected: {effectiveWallet.address.slice(0, 6)}...{effectiveWallet.address.slice(-4)}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Navigation />
        <Routes>
          <Route path="/" element={<Dashboard wallet={effectiveWallet} contracts={contracts} />} />
          <Route
            path="/upload"
            element={<ContentUpload wallet={effectiveWallet} contracts={contracts} />}
          />
          <Route
            path="/validate"
            element={<ContentValidation wallet={effectiveWallet} contracts={contracts} />}
          />
          <Route
            path="/profile"
            element={<Profile wallet={effectiveWallet} contracts={contracts} />}
          />
        </Routes>
      </Container>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DemoProvider>
        <Router>
          <AppContent />
        </Router>
      </DemoProvider>
    </ThemeProvider>
  );
}

export default App;
